import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  OutlinedInput,
  ListItemText,
  Avatar,
  Checkbox,
  Stack,
  Chip,
  Divider,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  AvatarGroup,
  InputAdornment,
} from "@mui/material";
import {
  Edit as EditIcon,
  Event as EventIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  Link as LinkIcon,
  DirectionsCar as DirectionsCarIcon,
  Videocam as VideocamIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpOutlineIcon,
  Group as GroupIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  AccessTime as AccessTimeIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { addHours, format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { useTranslation } from "react-i18next";

// Composant moderne d'information 
const InfoCard = ({ title, icon, children, sx = {} }) => (
  <Paper 
    elevation={0} 
    sx={{ 
      p: 2, 
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      ...sx 
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
      {icon}
      <Typography variant="subtitle1" fontWeight={600} sx={{ ml: 1 }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

// Composant pour afficher une information avec icône
const InfoItem = ({ icon, label, value, href = null }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, gap: 1 }}>
    {icon}
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
        {label}
      </Typography>
      {href ? (
        <Typography 
          component="a" 
          href={href} 
          target="_blank" 
          rel="noopener noreferrer"
          variant="body1"
          sx={{ 
            color: 'primary.main', 
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          {value}
        </Typography>
      ) : (
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {value}
        </Typography>
      )}
    </Box>
  </Box>
);

/**
 * Composant modal moderne pour créer, modifier ou voir un événement
 */
export default function EventModal({
  open,
  onClose,
  event,
  onCreate,
  onUpdate,
  onDelete,
  tags,
  groups,
  users,
  viewOnly = false,
}) {
  const { t } = useTranslation();
  const isEditMode = !!event;

  // Fonctions utilitaires avec traductions
  const getTransportLabel = useCallback((medio) => {
    const labels = {
      TREN: t('agenda.train'),
      AVION: t('agenda.plane'),
      COCHE_CLUB: t('agenda.clubCar'),
      COCHE_PERSONAL: t('agenda.personalCar'),
      BUS: t('agenda.bus')
    };
    return labels[medio] || medio;
  }, [t]);

  const getScoutingLabel = useCallback((tipo) => {
    const labels = {
      LIVE: t('agenda.live'),
      VIDEO: t('agenda.video')
    };
    return labels[tipo] || tipo;
  }, [t]);

  const getConfirmationDisplay = useCallback((confirmadoValue) => {
    if (confirmadoValue === "SI") {
      return {
        icon: <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />,
        text: t('agenda.confirmed'),
        color: "success"
      };
    } else if (confirmadoValue === "NO") {
      return {
        icon: <CancelIcon sx={{ fontSize: 18, color: 'error.main' }} />,
        text: t('agenda.notConfirmed'),
        color: "error"
      };
    } else {
      return {
        icon: <HelpOutlineIcon sx={{ fontSize: 18, color: 'grey.500' }} />,
        text: t('eventModal.unspecified'),
        color: "default"
      };
    }
  }, [t]);

  // Valeurs par défaut
  const getDefaultDates = useCallback(() => {
    const now = new Date();
    const defaultStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      now.getHours(),
      0
    );
    const defaultEnd = addHours(defaultStart, 2);
    return {
      start: defaultStart.toISOString().slice(0, 16),
      end: defaultEnd.toISOString().slice(0, 16)
    };
  }, []);

  // États du formulaire
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tagId, setTagId] = useState("");
  const [startDate, setStartDate] = useState(() => getDefaultDates().start);
  const [endDate, setEndDate] = useState(() => getDefaultDates().end);
  const [groupIds, setGroupIds] = useState([]);
  const [participantIds, setParticipantIds] = useState([]);
  const [confirmado, setConfirmado] = useState("");
  const [medioTransporte, setMedioTransporte] = useState("");
  const [tipoScouting, setTipoScouting] = useState("");
  const [url, setUrl] = useState("");
  const [ubicacion, setUbicacion] = useState("");

  // État pour le mode vue/édition
  const [isViewing, setIsViewing] = useState(viewOnly);
  const [errors, setErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Fonction pour vérifier si le formulaire a des changements non sauvegardés
  const checkForUnsavedChanges = useCallback(() => {
    if (isViewing) return false;
    
    if (!isEditMode) {
      // Nouveau événement - vérifier si des champs ont été modifiés
      const defaultDates = getDefaultDates();
      return title.trim() !== "" || 
             description.trim() !== "" || 
             tagId !== "" || 
             startDate !== defaultDates.start || 
             endDate !== defaultDates.end || 
             participantIds.length > 0 || 
             groupIds.length > 0 || 
             confirmado !== "" || 
             medioTransporte !== "" || 
             tipoScouting !== "" || 
             url.trim() !== "" || 
             ubicacion.trim() !== "";
    } else {
      // Édition d'événement - comparer avec les valeurs originales
      return title !== (event.title || "") ||
             description !== (event.description || "") ||
             tagId !== (event.tagId || "") ||
             startDate !== event.startDate.slice(0, 16) ||
             endDate !== event.endDate.slice(0, 16) ||
             JSON.stringify(participantIds.sort()) !== JSON.stringify((event.participants || []).map(p => p.id.toString()).sort()) ||
             JSON.stringify(groupIds.sort()) !== JSON.stringify((event.groupes || []).map(g => g.id.toString()).sort()) ||
             confirmado !== (event.confirmado || "") ||
             medioTransporte !== (event.medioTransporte || "") ||
             tipoScouting !== (event.tipoScouting || "") ||
             url !== (event.url || "") ||
             ubicacion !== (event.ubicacion || "");
    }
  }, [isViewing, isEditMode, title, description, tagId, startDate, endDate, participantIds, groupIds, confirmado, medioTransporte, tipoScouting, url, ubicacion, event, getDefaultDates]);

  // Gestionnaire de fermeture avec confirmation
  const handleCloseWithConfirmation = useCallback(() => {
    if (checkForUnsavedChanges()) {
      if (window.confirm(t('eventModal.unsavedChangesWarning'))) {
        onClose();
      }
    } else {
      onClose();
    }
  }, [checkForUnsavedChanges, onClose, t]);

  // Gestionnaire pour les clics en dehors de la modal
  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      handleCloseWithConfirmation();
    }
  }, [handleCloseWithConfirmation]);

  // Effet pour détecter les changements et mettre à jour l'état
  useEffect(() => {
    setHasUnsavedChanges(checkForUnsavedChanges());
  }, [checkForUnsavedChanges]);

  // Effet pour gérer la fermeture avec Escape et les changements d'onglets
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && open) {
        handleCloseWithConfirmation();
      }
    };

    const handleBeforeUnload = (e) => {
      if (open && checkForUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && open && checkForUnsavedChanges()) {
        // Optionnel: afficher une notification discrète
        console.warn('Hay cambios sin guardar en el evento');
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [open, handleCloseWithConfirmation, checkForUnsavedChanges]);
  const adjustEndDate = useCallback((newStartDate) => {
    const startDateTime = new Date(newStartDate);
    const endDateTime = new Date(endDate);
    
    // Si la date de début est postérieure à la date de fin, ajuster la date de fin
    if (startDateTime >= endDateTime) {
      const newEndDate = addHours(startDateTime, 2);
      setEndDate(newEndDate.toISOString().slice(0, 16));
    }
  }, [endDate]);

  // Handler pour la date de début avec ajustement automatique
  const handleStartDateChange = useCallback((e) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    adjustEndDate(newStartDate);
  }, [adjustEndDate]);

  // Validation
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!title.trim()) newErrors.title = t('eventModal.titleRequired');
    if (!tagId) newErrors.tagId = t('eventModal.tagRequired');
    if (participantIds.length === 0) newErrors.participantIds = t('eventModal.participantsRequired');
    if (new Date(endDate) <= new Date(startDate)) {
      newErrors.endDate = t('eventModal.endDateError');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [title, tagId, participantIds, endDate, startDate, t]);

  const resetForm = useCallback(() => {
    setTitle("");
    setDescription("");
    setTagId("");
    setGroupIds([]);
    setParticipantIds([]);
    setConfirmado("");
    setMedioTransporte("");
    setTipoScouting("");
    setUrl("");
    setUbicacion("");
    setErrors({});
    setHasUnsavedChanges(false);

    const { start, end } = getDefaultDates();
    setStartDate(start);
    setEndDate(end);
  }, [getDefaultDates]);

  // Charger les données de l'événement
  useEffect(() => {
    if (isEditMode && event) {
      setTitle(event.title || "");
      setDescription(event.description || "");
      setTagId(event.tagId || "");
      setStartDate(event.startDate.slice(0, 16));
      setEndDate(event.endDate.slice(0, 16));
      setGroupIds((event.groupes || []).map((g) => g.id.toString()));
      setParticipantIds((event.participants || []).map((p) => p.id.toString()));
      setConfirmado(event.confirmado || "");
      setMedioTransporte(event.medioTransporte || "");
      setTipoScouting(event.tipoScouting || "");
      setUrl(event.url || "");
      setUbicacion(event.ubicacion || "");
    } else {
      resetForm();
    }
    setIsViewing(viewOnly);
  }, [event, viewOnly, isEditMode, resetForm]);

  // Handlers
  const handleSave = useCallback(() => {
    if (!validateForm()) return;

    const newEvent = {
      title,
      description,
      tagId: Number(tagId),
      groupIds: groupIds.map((id) => Number(id)),
      participantIds: participantIds.map((id) => Number(id)),
      startDate,
      endDate,
      confirmado: confirmado || null,
      medioTransporte: medioTransporte || null,
      tipoScouting: tipoScouting || null,
      url: url || null,
      ubicacion: ubicacion || null,
    };

    if (isEditMode) {
      const merged = { ...event, ...newEvent };
      onUpdate(merged);
    } else {
      onCreate(newEvent);
    }
    setHasUnsavedChanges(false);
    onClose();
  }, [validateForm, title, description, tagId, groupIds, participantIds, startDate, endDate, confirmado, medioTransporte, tipoScouting, url, ubicacion, isEditMode, event, onUpdate, onCreate, onClose]);

  const handleDelete = useCallback(() => {
    if (!window.confirm(t('eventModal.deleteConfirm'))) {
      return;
    }
    if (!event) return;
    onDelete(event.id);
    setHasUnsavedChanges(false);
    onClose();
  }, [event, onDelete, onClose, t]);

  const handleGroupsChange = useCallback((e) => {
    const { target: { value } } = e;
    setGroupIds(typeof value === "string" ? value.split(",") : value);
  }, []);

  const handleUsersChange = useCallback((e) => {
    const { target: { value } } = e;
    setParticipantIds(typeof value === "string" ? value.split(",") : value);
  }, []);

  // Données dérivées
  const selectedTag = useMemo(() => 
    tags.find(t => t.id === Number(tagId)), 
    [tags, tagId]
  );

  const selectedUsers = useMemo(() => 
    users.filter(u => participantIds.includes(u.id.toString())),
    [users, participantIds]
  );

  const selectedGroups = useMemo(() => 
    groups.filter(g => groupIds.includes(g.id.toString())),
    [groups, groupIds]
  );

  // Vue en lecture seule moderne
  const ViewOnlyContent = useMemo(() => (
    <Stack spacing={3}>
      {/* En-tête avec titre et tag */}
      <Box>
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 2, 
            fontWeight: 700,
            color: selectedTag?.code_couleur || 'text.primary'
          }}
        >
          {title}
        </Typography>
        {selectedTag && (
          <Chip
            label={selectedTag.libelle}
            sx={{
              backgroundColor: selectedTag.code_couleur,
              color: '#fff',
              fontWeight: 600,
              '& .MuiChip-label': { color: '#fff' }
            }}
            size="medium"
          />
        )}
      </Box>

      <Divider />

      {/* Dates et confirmation en grid */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <InfoCard
            title={t('eventModal.datesAndSchedules')}
            icon={<EventIcon color="primary" />}
          >
            <InfoItem
              icon={<AccessTimeIcon sx={{ color: 'success.main', fontSize: 18 }} />}
              label={t('eventModal.start')}
              value={(() => {
                try {
                  const date = startDate.includes('T') ? parseISO(startDate) : new Date(startDate);
                  return format(date, "EEEE d MMMM yyyy 'a las' HH:mm", { locale: es });
                } catch {
                  return startDate;
                }
              })()}
            />
            <InfoItem
              icon={<AccessTimeIcon sx={{ color: 'error.main', fontSize: 18 }} />}
              label={t('eventModal.end')}
              value={(() => {
                try {
                  const date = endDate.includes('T') ? parseISO(endDate) : new Date(endDate);
                  return format(date, "EEEE d MMMM yyyy 'a las' HH:mm", { locale: es });
                } catch {
                  return endDate;
                }
              })()}
            />
          </InfoCard>
        </Grid>
        
        <Grid item xs={12} md={4}>
          {(() => {
            const confirmationDisplay = getConfirmationDisplay(confirmado);
            return (
              <InfoCard
                title={t('eventModal.state')}
                icon={confirmationDisplay.icon}
              >
                <Chip
                  label={confirmationDisplay.text}
                  color={confirmationDisplay.color}
                  variant="outlined"
                  size="small"
                />
              </InfoCard>
            );
          })()}
        </Grid>
      </Grid>

      {/* Description */}
      {description && (
        <InfoCard
          title={t('eventModal.description')}
          icon={<DescriptionIcon color="primary" />}
        >
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {description}
          </Typography>
        </InfoCard>
      )}

      {/* Participants avec avatars groupés */}
      <InfoCard
        title={`${t('eventModal.participants')} (${selectedUsers.length})`}
        icon={<PersonIcon color="primary" />}
      >
        <Box sx={{ mb: 2 }}>
          <AvatarGroup max={8} sx={{ justifyContent: 'flex-start' }}>
            {selectedUsers.map(u => (
              <Tooltip key={u.id} title={`${u.prenom} ${u.nom}`}>
                <Avatar
                  src={u.id ? `${process.env.REACT_APP_MEDIA_URL}/users/${u.id}.png?${new Date().getTime()}` : ""}
                  alt={`${u.nom} ${u.prenom}`}
                  sx={{ width: 40, height: 40 }}
                />
              </Tooltip>
            ))}
          </AvatarGroup>
        </Box>
        <Grid container spacing={1}>
          {selectedUsers.map(u => (
            <Grid item xs={12} sm={6} md={4} key={u.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1, '&:hover': { bgcolor: 'grey.50' } }}>
                <Avatar
                  src={u.id ? `${process.env.REACT_APP_MEDIA_URL}/users/${u.id}.png?${new Date().getTime()}` : ""}
                  alt={`${u.nom} ${u.prenom}`}
                  sx={{ width: 32, height: 32 }}
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {u.prenom} {u.nom}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </InfoCard>

      {/* Groupes */}
      {selectedGroups.length > 0 && (
        <InfoCard
          title={t('eventModal.groups')}
          icon={<GroupIcon color="primary" />}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedGroups.map(g => (
              <Chip 
                key={g.id} 
                label={g.libelle} 
                variant="filled"
                color="primary"
                size="small"
              />
            ))}
          </Box>
        </InfoCard>
      )}

      {/* Informations supplémentaires */}
      {(ubicacion || url || medioTransporte || tipoScouting) && (
        <InfoCard
          title={t('eventModal.additionalInfo')}
          icon={<LocationOnIcon color="primary" />}
        >
          <Grid container spacing={2}>
            {ubicacion && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                  <LocationOnIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      {t('eventModal.location')}
                    </Typography>
                    <Typography 
                      component="a" 
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ubicacion)}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      variant="body1"
                      sx={{ 
                        color: 'primary.main', 
                        textDecoration: 'none',
                        fontWeight: 500,
                        cursor: 'pointer',
                        '&:hover': { 
                          textDecoration: 'underline',
                          color: 'primary.dark'
                        }
                      }}
                    >
                      {ubicacion}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            
            {url && (
              <Grid item xs={12} sm={6}>
                <InfoItem
                  icon={<LinkIcon sx={{ color: 'primary.main', fontSize: 18 }} />}
                  label={t('eventModal.link')}
                  value={url}
                  href={url}
                />
              </Grid>
            )}
            
            {medioTransporte && (
              <Grid item xs={12} sm={6}>
                <InfoItem
                  icon={<DirectionsCarIcon sx={{ color: 'primary.main', fontSize: 18 }} />}
                  label={t('eventModal.transport')}
                  value={getTransportLabel(medioTransporte)}
                />
              </Grid>
            )}
            
            {tipoScouting && (
              <Grid item xs={12} sm={6}>
                <InfoItem
                  icon={<VideocamIcon sx={{ color: 'primary.main', fontSize: 18 }} />}
                  label={t('eventModal.scoutingType')}
                  value={getScoutingLabel(tipoScouting)}
                />
              </Grid>
            )}
          </Grid>
        </InfoCard>
      )}
    </Stack>
  ), [selectedTag, title, startDate, endDate, description, selectedUsers, selectedGroups, ubicacion, url, medioTransporte, tipoScouting, confirmado, t, getTransportLabel, getScoutingLabel, getConfirmationDisplay]);

  // Vue d'édition moderne
  const EditContent = useMemo(() => (
    <Stack spacing={3}>
      {/* Alertes d'erreur */}
      {Object.keys(errors).length > 0 && (
        <Alert severity="error">
          {t('eventModal.formErrors')}
        </Alert>
      )}

      {/* Informations principales */}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('eventModal.mainInfo')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={t('eventModal.title')}
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              error={!!errors.title}
              helperText={errors.title}
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required error={!!errors.tagId}>
              <InputLabel>{t('eventModal.tag')}</InputLabel>
              <Select
                label={t('eventModal.tag')}
                value={tagId || ""}
                onChange={(e) => setTagId(e.target.value)}
              >
                <MenuItem value="">
                  <em>{t('eventModal.selectTag')}</em>
                </MenuItem>
                {tags.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          backgroundColor: t.code_couleur,
                          borderRadius: '4px',
                        }}
                      />
                      {t.libelle}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.tagId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {errors.tagId}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('eventModal.confirmed')}</InputLabel>
              <Select
                label={t('eventModal.confirmed')}
                value={confirmado}
                onChange={(e) => setConfirmado(e.target.value)}
              >
                <MenuItem value="">{t('eventModal.unspecified')}</MenuItem>
                <MenuItem value="SI">{t('agenda.confirmed')}</MenuItem>
                <MenuItem value="NO">{t('agenda.notConfirmed')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Fechas */}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('eventModal.datesAndSchedules')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('eventModal.start')}
              type="datetime-local"
              fullWidth
              value={startDate}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              required
              helperText={t('eventModal.startHelper')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('eventModal.end')}
              type="datetime-local"
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
              error={!!errors.endDate}
              helperText={errors.endDate}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Participants et groupes */}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('eventModal.participantsAndGroups')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required error={!!errors.participantIds}>
              <InputLabel>{t('eventModal.participants')}</InputLabel>
              <Select
                multiple
                value={participantIds}
                onChange={handleUsersChange}
                input={<OutlinedInput label={t('eventModal.participants')} />}
                renderValue={(selected) => {
                  const selectedUsers = users.filter((u) => selected.includes(u.id.toString()));
                  if (selectedUsers.length === 0) return "";
                  if (selectedUsers.length === 1) {
                    const user = selectedUsers[0];
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={user.id ? `${process.env.REACT_APP_MEDIA_URL}/users/${user.id}.png?${new Date().getTime()}` : ""}
                          alt={`${user.nom} ${user.prenom}`}
                          sx={{ width: 24, height: 24 }}
                        />
                        {`${user.prenom} ${user.nom}`}
                      </Box>
                    );
                  }
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                        {selectedUsers.slice(0, 3).map(user => (
                          <Avatar
                            key={user.id}
                            src={user.id ? `${process.env.REACT_APP_MEDIA_URL}/users/${user.id}.png?${new Date().getTime()}` : ""}
                            alt={`${user.nom} ${user.prenom}`}
                          />
                        ))}
                      </AvatarGroup>
                      <Typography variant="body2">
                        {selectedUsers.length === 2
                          ? `${selectedUsers[0].prenom} ${selectedUsers[0].nom} ${t('eventModal.and')} ${selectedUsers[1].prenom} ${selectedUsers[1].nom}`
                          : `${selectedUsers[0].prenom} ${selectedUsers[0].nom} ${t('eventModal.and')} ${selectedUsers.length - 1} ${t('eventModal.more')}`
                        }
                      </Typography>
                    </Box>
                  );
                }}
              >
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id.toString()}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                      <Avatar
                        src={u.id ? `${process.env.REACT_APP_MEDIA_URL}/users/${u.id}.png?${new Date().getTime()}` : ""}
                        alt={`${u.nom} ${u.prenom}`}
                        sx={{ width: 32, height: 32 }}
                      />
                      <Checkbox 
                        checked={participantIds.includes(u.id.toString())} 
                        sx={{ p: 0.5 }}
                      />
                      <ListItemText 
                        primary={`${u.prenom} ${u.nom}`}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.participantIds && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {errors.participantIds}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t('eventModal.groups')}</InputLabel>
              <Select
                multiple
                value={groupIds}
                onChange={handleGroupsChange}
                input={<OutlinedInput label={t('eventModal.groups')} />}
                renderValue={(selected) => {
                  const labels = groups
                    .filter((g) => selected.includes(g.id.toString()))
                    .map((g) => g.libelle);
                  return labels.join(", ");
                }}
              >
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id.toString()}>
                    <Checkbox checked={groupIds.includes(g.id.toString())} />
                    <ListItemText primary={g.libelle} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Description */}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('eventModal.description')}
        </Typography>
        <TextField
          label={t('eventModal.description')}
          fullWidth
          multiline
          minRows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('eventModal.descriptionPlaceholder')}
        />
      </Paper>

      {/* Informations supplémentaires */}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('eventModal.additionalInfo')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('eventModal.location')}
              fullWidth
              value={ubicacion}
              onChange={(e) => setUbicacion(e.target.value)}
              placeholder={t('eventModal.locationPlaceholder')}
              helperText={t('eventModal.locationHelper')}
              InputProps={{
                endAdornment: ubicacion && (
                  <Tooltip title={t('eventModal.viewInMaps')}>
                    <IconButton
                      size="small"
                      onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ubicacion)}`, '_blank')}
                    >
                      <LocationOnIcon color="primary" />
                    </IconButton>
                  </Tooltip>
                )
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={t('eventModal.url')}
              fullWidth
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('eventModal.transportMeans')}</InputLabel>
              <Select
                label={t('eventModal.transportMeans')}
                value={medioTransporte}
                onChange={(e) => setMedioTransporte(e.target.value)}
              >
                <MenuItem value="">{t('eventModal.none')}</MenuItem>
                <MenuItem value="TREN">{t('agenda.train')}</MenuItem>
                <MenuItem value="AVION">{t('agenda.plane')}</MenuItem>
                <MenuItem value="COCHE_CLUB">{t('agenda.clubCar')}</MenuItem>
                <MenuItem value="COCHE_PERSONAL">{t('agenda.personalCar')}</MenuItem>
                <MenuItem value="BUS">{t('agenda.bus')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('eventModal.scoutingType')}</InputLabel>
              <Select
                label={t('eventModal.scoutingType')}
                value={tipoScouting}
                onChange={(e) => setTipoScouting(e.target.value)}
              >
                <MenuItem value="">{t('eventModal.none')}</MenuItem>
                <MenuItem value="LIVE">{t('agenda.live')}</MenuItem>
                <MenuItem value="VIDEO">{t('agenda.video')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
    </Stack>
  ), [title, tagId, tags, startDate, endDate, participantIds, handleUsersChange, users, description, groupIds, handleGroupsChange, groups, ubicacion, url, confirmado, medioTransporte, tipoScouting, errors, handleStartDateChange, t]);

  return (
    <Modal open={open} onClose={handleCloseWithConfirmation}>
      <Box 
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95vw', sm: '90vw', md: isViewing ? '80vw' : '70vw' },
          maxWidth: isViewing ? 1000 : 800,
          maxHeight: '95vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
        }}
        onClick={handleBackdropClick}
      >
        <Box onClick={(e) => e.stopPropagation()}>
          {/* Header moderne */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            p: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" fontWeight={700}>
                {isViewing
                  ? t('eventModal.eventDetails')
                  : isEditMode
                    ? t('eventModal.editEvent')
                    : t('eventModal.newEvent')}
              </Typography>
              {hasUnsavedChanges && !isViewing && (
                <Chip
                  label={t('eventModal.unsavedChanges')}
                  color="warning"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isViewing && isEditMode && (
                <Tooltip title={t('eventModal.editEvent')}>
                  <IconButton
                    color="primary"
                    onClick={() => setIsViewing(false)}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('eventModal.close')}>
                <IconButton onClick={handleCloseWithConfirmation}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Contenu */}
          <Box sx={{ p: 3 }}>
            {isViewing ? ViewOnlyContent : EditContent}
          </Box>

          {/* Footer avec actions */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            p: 3,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50'
          }}>
            <Box>
              {!isViewing && isEditMode && (
                <Button 
                  color="error" 
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  variant="outlined"
                >
                  {t('eventModal.delete')}
                </Button>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={handleCloseWithConfirmation} variant="outlined">
                {isViewing ? t('eventModal.close') : t('eventModal.cancel')}
              </Button>
              {!isViewing && (
                <Button 
                  variant="contained" 
                  onClick={handleSave}
                  startIcon={<SaveIcon />}
                  sx={{ minWidth: 120 }}
                >
                  {isEditMode ? t('eventModal.save') : t('eventModal.create')}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}